/**
 * The listicle hub.
 *
 * Each row carries the list's own standfirst rather than a written-for-the-hub
 * teaser. The standfirst is already the one line that says what the list is
 * for, so a second version of it would be a second thing to keep true, and the
 * one that goes stale is always the copy nobody reads while editing the other.
 *
 * The `pick` is deliberately not here. It is the answer and it is a paragraph:
 * long enough to make every row a wall, and it belongs on the page where the
 * evidence for it is.
 */

import type { ContentLang } from "@fazly/shared/contentLang";
import { Link } from "@tanstack/react-router";

import { BEST_SLUGS, bestPath } from "../../lib/best/index";
import type { BestCopy, BestSlug } from "../../lib/best/types";
import type { BestUi } from "../../lib/best/ui";
import { toolsIndexPath } from "../../lib/tools/index";
import { ContentFooter } from "../ContentFooter";

export function BestIndex({
  lang,
  ui,
  copy,
}: {
  lang: ContentLang;
  ui: BestUi;
  copy: Record<BestSlug, BestCopy>;
}) {
  return (
    <main className="mx-auto w-full max-w-3xl px-6 py-12 sm:py-16">
      <h1 className="font-display text-[clamp(2rem,7vw,3rem)] leading-[1.1] font-bold tracking-tight text-ink">
        {ui.indexH1}
      </h1>
      <p className="mt-4 max-w-xl text-lg leading-relaxed text-ink2">
        {ui.indexStandfirst}
      </p>

      <ul className="mt-10 divide-y divide-hair border-y-[2.5px] border-ink">
        {BEST_SLUGS.map((slug) => (
          <li key={slug}>
            <Link
              to={bestPath(slug, lang)}
              className="group flex flex-col gap-1 py-5 transition-colors hover:bg-inset"
            >
              <span className="font-display text-xl font-bold text-ink group-hover:text-brand">
                {copy[slug].h1}
              </span>
              <span className="text-[16px] leading-relaxed text-ink2">
                {copy[slug].standfirst}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <p className="mt-8 text-[17px] leading-relaxed text-body">
        {ui.tryFirst}
      </p>
      <Link
        to={toolsIndexPath(lang)}
        className="rim mt-4 inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
      >
        {ui.toolsCta}
      </Link>

      <ContentFooter lang={lang} />
    </main>
  );
}
